import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { productsAdminApi, type ProductAdminRow } from "@/lib/products-admin";
import { useProductCategoryTree, productKeys } from "@/lib/products";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { DetailModal } from "@/components/detail-modal";
import { EntityForm } from "@/components/entity-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RowActions } from "@/components/row-actions";

// ---------------------------------------------------------------------------
// Zod schema
// ---------------------------------------------------------------------------

const productSchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  sku: z.string().max(80).nullable().optional(),
  description: z.string().nullable().optional(),
  extendedDescription: z.string().nullable().optional(),
  thumbnailUrl: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  categoryId: z.number().int().nullable().optional(),
  supplierId: z.number().int().nullable().optional(),
});

type ProductForm = z.infer<typeof productSchema>;

const BLANK_FORM: ProductForm = {
  name: "",
  sku: null,
  description: null,
  extendedDescription: null,
  thumbnailUrl: null,
  imageUrl: null,
  categoryId: null,
  supplierId: null,
};

// ---------------------------------------------------------------------------
// Query key for the admin list
// ---------------------------------------------------------------------------

const ADMIN_LIST_KEY = ["products-admin"] as const;

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function ProductsAdminPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ProductAdminRow | null>(null);

  // List
  const { data, isLoading } = useQuery({
    queryKey: [...ADMIN_LIST_KEY, page, q],
    queryFn: () => productsAdminApi.listAdmin(q, page + 1),
  });

  // Category tree (for the dropdown)
  const { data: categories } = useProductCategoryTree();

  // Supplier list (for the dropdown)
  const { data: suppliers } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () => productsAdminApi.suppliers(),
  });

  // Create / update mutation
  const upsert = useMutation({
    mutationFn: (form: ProductForm) => {
      const body = {
        name: form.name,
        sku: form.sku ?? null,
        description: form.description ?? null,
        extendedDescription: form.extendedDescription ?? null,
        thumbnailUrl: form.thumbnailUrl ?? null,
        imageUrl: form.imageUrl ?? null,
        categoryId: form.categoryId ?? null,
        supplierId: form.supplierId ?? null,
      };
      if (editing) {
        return productsAdminApi.update(editing.productId, body, editing.rowVersionBase64);
      }
      return productsAdminApi.create(body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_LIST_KEY });
      void qc.invalidateQueries({ queryKey: productKeys.categoryTree() });
      void qc.invalidateQueries({ queryKey: ["products"] });
      setCreating(false);
      setEditing(null);
    },
  });

  // Delete mutation
  const remove = useMutation({
    mutationFn: (row: ProductAdminRow) =>
      productsAdminApi.remove(row.productId, row.rowVersionBase64),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_LIST_KEY });
      void qc.invalidateQueries({ queryKey: productKeys.categoryTree() });
      void qc.invalidateQueries({ queryKey: ["products"] });
    },
  });

  // Resolve category name for display
  const categoryMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of categories ?? []) m.set(c.id, c.name);
    return m;
  }, [categories]);

  // Columns
  const cols: DataGridColumn<ProductAdminRow>[] = [
    { id: "name", header: "Name", sortable: true, accessor: (r) => r.name },
    { id: "sku", header: "SKU", accessor: (r) => r.sku ?? "—" },
    {
      id: "category",
      header: "Category",
      accessor: (r) =>
        r.categoryId != null ? (categoryMap.get(r.categoryId) ?? String(r.categoryId)) : "—",
    },
    {
      id: "status",
      header: "Status",
      accessor: (r) => (r.deleted ? "(deleted)" : "Active"),
    },
    {
      id: "actions",
      header: "",
      accessor: (r) => (
        <div className="flex justify-end pr-2">
          <RowActions
            label={r.name}
            onEdit={() => setEditing(r)}
            onDelete={() => remove.mutate(r)}
            deleteDisabled={remove.isPending}
          />
        </div>
      ),
      align: "right",
    },
  ];

  // Default form values when editing
  function toFormValues(row: ProductAdminRow): ProductForm {
    return {
      name: row.name,
      sku: row.sku,
      description: row.description ?? "",
      extendedDescription: row.extendedDescription ?? "",
      thumbnailUrl: row.thumbnailUrl ?? "",
      imageUrl: row.imageUrl ?? "",
      categoryId: row.categoryId,
      supplierId: row.supplierId,
    };
  }

  const drawerOpen = creating || editing !== null;
  const drawerTitle = editing ? "Edit product" : "New product";

  return (
    <ListPage backTo="/admin"
      title="Products"
      primaryAction={
        <Button onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> Add Product</Button>
      }
      search={{
        value: q,
        onChange: (v) => { setQ(v); setPage(0); },
        placeholder: "Search name or SKU…",
      }}
    >
      <DataGrid
        rows={data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.productId}
        totalRows={data?.total ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        onPageChange={setPage}
      />

      <DetailModal
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
        title={drawerTitle}
      >
        <EntityForm
          schema={productSchema}
          defaultValues={editing ? toFormValues(editing) : BLANK_FORM}
          onSubmit={(v) => upsert.mutate(v)}
        >
          {(form) => (
            <div className="space-y-3">
              {/* Name */}
              <div className="space-y-1">
                <Label htmlFor="prod-name">Name</Label>
                <Input id="prod-name" {...form.register("name")} />
              </div>

              {/* SKU */}
              <div className="space-y-1">
                <Label htmlFor="prod-sku">SKU</Label>
                <Input id="prod-sku" {...form.register("sku")} />
              </div>

              {/* Description */}
              <div className="space-y-1">
                <Label htmlFor="prod-desc">Description</Label>
                <Input id="prod-desc" {...form.register("description")} />
              </div>

              {/* Extended description */}
              <div className="space-y-1">
                <Label htmlFor="prod-ext-desc">Extended description</Label>
                <Input id="prod-ext-desc" {...form.register("extendedDescription")} />
              </div>

              {/* Thumbnail URL */}
              <div className="space-y-1">
                <Label htmlFor="prod-thumb">Thumbnail URL</Label>
                <Input id="prod-thumb" {...form.register("thumbnailUrl")} />
              </div>

              {/* Image URL */}
              <div className="space-y-1">
                <Label htmlFor="prod-img">Image URL</Label>
                <Input id="prod-img" {...form.register("imageUrl")} />
              </div>

              {/* Category dropdown */}
              <div className="space-y-1">
                <Label htmlFor="prod-cat">Category</Label>
                <select
                  id="prod-cat"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  {...form.register("categoryId", { setValueAs: (v: string) => v === "" ? null : Number(v) })}
                >
                  <option value="">— None —</option>
                  {(categories ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Supplier dropdown */}
              <div className="space-y-1">
                <Label htmlFor="prod-sup">Supplier</Label>
                <select
                  id="prod-sup"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  {...form.register("supplierId", { setValueAs: (v: string) => v === "" ? null : Number(v) })}
                >
                  <option value="">— None —</option>
                  {(suppliers ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <Button type="submit">Save</Button>
            </div>
          )}
        </EntityForm>
      </DetailModal>
    </ListPage>
  );
}
